import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export type ChallengeScope = 'league' | 'international'
export type ChallengeVisibility = 'public' | 'private'

export interface ChallengeIcon {
  type?: string | null
  url?: string | null
  image_path?: string | null
  name?: string | null
  league_id?: number | null
  league_name?: string | null
  country_id?: number | null
  country_name?: string | null
  [key: string]: unknown
}

export interface ChallengeRuleClub {
  team_id: number
  name: string
  image_path?: string | null
}

export interface ChallengeRuleSeason {
  season_id: number
  name?: string | null
  league_id?: number | null
  league_name?: string | null
  country_id?: number | null
}

export interface ChallengeRule {
  type: string
  description?: string | null
  config?: {
    clubs?: ChallengeRuleClub[]
    seasons?: ChallengeRuleSeason[]
    [key: string]: unknown
  }
}

export interface FantasyChallenge {
  id: number
  title: string
  description?: string | null
  default_formation?: string | null
  formation?: string | null
  scope_type: ChallengeScope
  visibility?: ChallengeVisibility | null
  is_active: boolean
  is_highlighted?: boolean
  participants_count?: number
  status: 'open' | 'scoring' | 'completed' | string
  is_finished?: boolean
  submission_deadline_at?: string | null
  finished_at?: string | null
  results_announced_at?: string | null
  rules: ChallengeRule[]
  icons?: ChallengeIcon[]
  league?: {
    app_round_id?: number | null
    app_round_name?: string | null
    season_id?: number | null
  } | null
  international?: {
    window_starts_at?: string | null
    window_ends_at?: string | null
  } | null
}

export interface ChallengePlayer {
  player_id: number
  name: string
  image_path?: string | null
  team_id?: number | null
  team_name?: string | null
  team_display_name?: string | null
  position_group?: string | null
  age?: number | null
  height_cm?: number | null
  birth_country_id?: number | null
  detailed_position_id?: number | null
  preferred_foot?: 'left' | 'right' | 'both' | string | null
  is_captain?: boolean
  on_loan?: boolean
  minutes_played?: number
  goals?: number
  assists?: number
  fantasy_points?: number
  jersey_number?: number | null
  slot?: string | null
  slot_index?: number | null
  is_selectable?: boolean
  selection_status_message?: string | null
  is_selected?: boolean
  points?: number | null
  [key: string]: unknown
}

export interface ChallengeTeam {
  id: number
  name: string
  display_name?: string | null
}

export interface ChallengeFormation {
  key: string
  code?: string | null
  label: string
  lineup_size: number
  slots?: Record<string, number>
  is_default?: boolean
}

export interface ChallengeLineup {
  default_formation?: string | null
  selected_formation?: string | null
  lineup_size: number
  selected_slots_count?: number
  entry_completed?: boolean
  is_submitted?: boolean
  can_save_draft?: boolean
  position_groups?: string[]
  formations: ChallengeFormation[]
}

export interface ChallengeEntry {
  id?: number
  formation?: string | null
  is_submitted?: boolean
  players: ChallengePlayer[]
  [key: string]: unknown
}

export interface ChallengePlayersResponse {
  challenge_id: number
  lineup: ChallengeLineup
  entry?: ChallengeEntry | null
  teams: ChallengeTeam[]
  players: ChallengePlayer[]
  pagination?: {
    page: number
    per_page: number
    total: number
    last_page: number
    has_more: boolean
  }
}

export interface ChallengeEntryResponse {
  lineup?: ChallengeLineup
  entry?: ChallengeEntry | null
  score?: ChallengeScore | null
}

export interface ChallengeScore {
  total_points?: number
  player_scores?: Array<{
    slot?: string
    slot_index?: number
    player_id: number
    points?: number
  }>
  [key: string]: unknown
}

export interface ChallengeRankingRow {
  rank?: number
  user_id?: number
  display_name?: string | null
  profile_image?: string | null
  total_points?: number
  [key: string]: unknown
}

export interface ChallengeDashboard {
  total_entries?: number
  my_rank?: number | null
  total_points?: number
  is_final?: boolean
  is_finished?: boolean
  winners?: ChallengeRankingRow[]
  best_player?: ChallengePlayer | null
}

export interface ChallengeBreakdownResponse {
  challenge?: FantasyChallenge
  player?: ChallengePlayer
  matches?: Array<Record<string, unknown>>
  [key: string]: unknown
}

export interface ChallengeCountry {
  id: number
  name: string
  iso2?: string | null
  iso3?: string | null
  flag_url?: string | null
}

export interface ChallengeCompetition {
  id: number
  name: string
  logo_url?: string | null
}

export interface ChallengeSeason {
  id: number
  name: string
}

export interface ChallengeClub {
  id: number
  name: string
  logo_url?: string | null
}

export interface ChallengeAppRound {
  id: number
  label: string
}

export interface CreateChallengePayload {
  title: Record<string, string>
  description: Record<string, string>
  scope_type: ChallengeScope
  visibility: ChallengeVisibility
  country_id?: number | undefined
  league_id?: number | undefined
  season_id?: number | undefined
  app_round_id?: number | undefined
  window_starts_at?: string | undefined
  window_ends_at?: string | undefined
  submission_deadline_at?: string | undefined
  rule_type?: '' | 'team_whitelist' | 'standings_bottom_n' | 'standings_top_n' | undefined
  rule_n?: number | undefined
  rule_team_ids?: number[] | undefined
  international_sources?: Array<{
    country_id: number
    league_id: number
    season_id: number
    all_teams: boolean
    team_ids?: number[] | undefined
  }> | undefined
  nationality_ids?: number[] | undefined
  birth_country_ids?: number[] | undefined
  min_age?: number | undefined
  max_age?: number | undefined
  min_height_cm?: number | undefined
  max_height_cm?: number | undefined
  detailed_position_ids?: number[] | undefined
  preferred_feet?: Array<'left' | 'right' | 'both'> | undefined
  min_minutes?: number | undefined
  min_goals?: number | undefined
  min_assists?: number | undefined
  min_fantasy_points?: number | undefined
  captain_only?: boolean | undefined
  loan_only?: boolean | undefined
  max_players_per_club?: number | undefined
  min_distinct_clubs?: number | undefined
  is_active: boolean
  // Highlighting remains an admin-only backend setting; the SPA create flow does not expose it.
  is_highlighted?: boolean | undefined
}

function queryPath(path: string, query: Record<string, string | number | undefined>) {
  const params = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value))
  })
  const queryString = params.toString()
  return queryString ? `${path}?${queryString}` : path
}

export async function fetchFantasyChallenges(scopeType?: ChallengeScope) {
  const response = await api.get<{ challenges: FantasyChallenge[] }>(
    queryPath('/fantasy-challenges', { scope_type: scopeType }),
  )
  return response.challenges ?? []
}

export async function fetchFantasyChallenge(id: number) {
  const response = await api.get<{ challenge: FantasyChallenge }>(`/fantasy-challenges/${id}`)
  return response.challenge
}

export async function fetchChallengePlayers(
  id: number,
  filters: { slot?: string | undefined; q?: string | undefined; teamId?: number | undefined; page?: number | undefined; perPage?: number | undefined } = {},
) {
  return api.get<ChallengePlayersResponse>(
    queryPath(`/fantasy-challenges/${id}/players`, {
      slot: filters.slot,
      q: filters.q,
      team_id: filters.teamId,
      page: filters.page,
      per_page: filters.perPage,
    }),
  )
}

export async function fetchChallengeEntry(id: number) {
  return api.get<ChallengeEntryResponse>(`/fantasy-challenges/${id}/entry/me`)
}

export async function saveChallengeDraft(id: number, payload: { formation: string; players: ChallengeSelection[] }) {
  return api.put<{ saved: boolean; draft_saved?: boolean; lineup?: ChallengeLineup; entry?: ChallengeEntry }>(
    `/fantasy-challenges/${id}/entry/draft`,
    payload,
  )
}

export async function submitChallengeEntry(id: number, payload: { formation: string; players: ChallengeSelection[] }) {
  return api.put<{ entry: ChallengeEntry }>(`/fantasy-challenges/${id}/entry`, payload)
}

export async function fetchChallengeRanking(id: number) {
  return api.get<{ ranking: ChallengeRankingRow[] }>(`/fantasy-challenges/${id}/ranking`)
}

export async function fetchChallengeDashboard(id: number) {
  return api.get<{ challenge: FantasyChallenge; dashboard: ChallengeDashboard }>(`/fantasy-challenges/${id}/dashboard`)
}

export async function fetchChallengeBreakdown(id: number, playerId: number) {
  return api.get<ChallengeBreakdownResponse>(`/fantasy-challenges/${id}/players/${playerId}/breakdown`)
}

export interface ChallengeSelection {
  slot: string
  slot_index: number
  player_id: number
}

export function useFantasyChallenges(scopeType?: ChallengeScope) {
  return useQuery({
    queryKey: ['fantasy-challenges', scopeType ?? 'all'],
    queryFn: () => fetchFantasyChallenges(scopeType),
    staleTime: 60_000,
  })
}

export function useFantasyChallenge(id: number) {
  return useQuery({
    queryKey: ['fantasy-challenge', id],
    queryFn: () => fetchFantasyChallenge(id),
    enabled: Number.isFinite(id),
  })
}

export function useChallengePlayers(
  id: number,
  filters: { slot?: string | undefined; q?: string | undefined; teamId?: number | undefined; page?: number | undefined; perPage?: number | undefined } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['fantasy-challenge-players', id, filters],
    queryFn: () => fetchChallengePlayers(id, filters),
    enabled: enabled && Number.isFinite(id),
    staleTime: 30_000,
  })
}

export function useChallengeEntry(id: number) {
  return useQuery({
    queryKey: ['fantasy-challenge-entry', id],
    queryFn: () => fetchChallengeEntry(id),
    enabled: Number.isFinite(id),
  })
}

export function useSaveChallengeDraft(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { formation: string; players: ChallengeSelection[] }) => saveChallengeDraft(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fantasy-challenge-entry', id] })
      void queryClient.invalidateQueries({ queryKey: ['fantasy-challenge-players', id] })
    },
  })
}

export function useSubmitChallengeEntry(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { formation: string; players: ChallengeSelection[] }) => submitChallengeEntry(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fantasy-challenge-entry', id] })
      void queryClient.invalidateQueries({ queryKey: ['fantasy-challenge-players', id] })
      void queryClient.invalidateQueries({ queryKey: ['fantasy-challenge-ranking', id] })
    },
  })
}

export function useChallengeRanking(id: number, enabled = false) {
  return useQuery({
    queryKey: ['fantasy-challenge-ranking', id],
    queryFn: () => fetchChallengeRanking(id),
    enabled: enabled && Number.isFinite(id),
  })
}

export function useChallengeDashboard(id: number, enabled = false) {
  return useQuery({
    queryKey: ['fantasy-challenge-dashboard', id],
    queryFn: () => fetchChallengeDashboard(id),
    enabled: enabled && Number.isFinite(id),
  })
}

export function useChallengeBreakdown(id: number, playerId: number | null) {
  return useQuery({
    queryKey: ['fantasy-challenge-breakdown', id, playerId],
    queryFn: () => fetchChallengeBreakdown(id, playerId!),
    enabled: playerId !== null && Number.isFinite(id),
  })
}

export async function fetchChallengeCountries() {
  const response = await api.get<{ countries: ChallengeCountry[] }>('/countries')
  return response.countries ?? []
}

export async function fetchChallengeCompetitions(countryId: number) {
  try {
    const response = await api.get<{ competitions: ChallengeCompetition[] }>(
      queryPath('/fantasy-challenges/admin/competitions', { country_id: countryId }),
    )
    if (response.competitions?.length) return response.competitions
  } catch {
    // The public catalog is a safe fallback when the admin metadata endpoint
    // is unavailable or its admin claim has not propagated yet.
  }

  const fallback = await api.get<{ competitions: ChallengeCompetition[] }>(
    queryPath('/competitions', { country_id: countryId }),
  )
  return fallback.competitions ?? []
}

export async function fetchChallengeSeasons(leagueId: number, countryId?: number) {
  try {
    const response = await api.get<{ seasons: ChallengeSeason[] }>(
      queryPath('/fantasy-challenges/admin/seasons', { league_id: leagueId, country_id: countryId, current_only: 1 }),
    )
    if (response.seasons?.length) return response.seasons
  } catch {
    // Fall through to the public competition catalog for older deployments.
  }

  const fallback = await api.get<{
    countries?: Array<{
      competitions?: Array<{ id: number; season_id?: number }>
    }>
  }>('/explore/competitions/grouped-by-country')
  const competition = (fallback.countries ?? [])
    .flatMap((country) => country.competitions ?? [])
    .find((item) => item.id === leagueId)

  return competition?.season_id ? [{ id: competition.season_id, name: '' }] : []
}

export async function fetchChallengeClubs(countryId: number, leagueId: number, seasonId: number) {
  const response = await api.get<{ clubs: ChallengeClub[] }>(
    queryPath('/fantasy-challenges/admin/clubs', {
      country_id: countryId,
      league_id: leagueId,
      season_id: seasonId,
    }),
  )
  return response.clubs ?? []
}

export async function fetchChallengeAppRounds(seasonId: number, leagueId?: number) {
  try {
    const response = await api.get<{ app_rounds: ChallengeAppRound[] }>(
      queryPath('/fantasy-challenges/admin/app-rounds', { season_id: seasonId, league_id: leagueId, upcoming_only: 1 }),
    )
    return response.app_rounds ?? []
  } catch {
    // Keep compatibility with an older backend that does not yet accept the
    // league guard or upcoming_only parameter on this metadata endpoint.
    const fallback = await api.get<{ app_rounds: ChallengeAppRound[] }>(
      queryPath('/fantasy-challenges/admin/app-rounds', { season_id: seasonId, upcoming_only: 1 }),
    )
    return fallback.app_rounds ?? []
  }
}

export async function createFantasyChallenge(payload: CreateChallengePayload) {
  return api.post<{ challenge: FantasyChallenge }>('/fantasy-challenges', payload)
}

export type ChallengePoolPreviewPayload = Pick<
  CreateChallengePayload,
  | 'scope_type'
  | 'country_id'
  | 'league_id'
  | 'season_id'
  | 'app_round_id'
  | 'window_starts_at'
  | 'window_ends_at'
  | 'rule_type'
  | 'rule_n'
  | 'rule_team_ids'
  | 'international_sources'
  | 'nationality_ids'
  | 'birth_country_ids'
  | 'min_age'
  | 'max_age'
  | 'min_height_cm'
  | 'max_height_cm'
  | 'detailed_position_ids'
  | 'preferred_feet'
  | 'min_minutes'
  | 'min_goals'
  | 'min_assists'
  | 'min_fantasy_points'
  | 'captain_only'
  | 'loan_only'
  | 'max_players_per_club'
  | 'min_distinct_clubs'
>

export interface ChallengePoolPreview {
  total: number
  by_position: Record<string, number>
  formation: { key: string; slots: Record<string, number>; lineup_size: number }
}

export async function fetchChallengePoolPreview(payload: ChallengePoolPreviewPayload) {
  return api.post<ChallengePoolPreview>('/fantasy-challenges/preview-pool', payload)
}

// Live preview tijdens het aanmaken: laat zien hoeveel spelers er per positie overblijven
// onder de huidige concept-regels, zodat je kunt zien dat een challenge te krap wordt (bv.
// nog maar 1 keeper beschikbaar) voordat je 'm daadwerkelijk aanmaakt.
export function useFantasyChallengePoolPreview(payload: ChallengePoolPreviewPayload, enabled: boolean) {
  return useQuery({
    queryKey: ['fantasy-challenge-pool-preview', payload],
    queryFn: () => fetchChallengePoolPreview(payload),
    enabled,
    staleTime: 10_000,
  })
}

export interface ChallengeShare {
  type: string
  title: string
  share_url: string
  app_url: string
  share_message: string
  resource?: Record<string, unknown>
}

export async function fetchChallengeShare(id: number) {
  return api.get<ChallengeShare>(`/fantasy-challenges/${id}/share`)
}

export function useShareFantasyChallenge(id: number | undefined) {
  return useMutation({
    mutationFn: () => fetchChallengeShare(id as number),
  })
}

export function useFantasyChallengeCountries(enabled = true) {
  return useQuery({ queryKey: ['fantasy-challenge-create-countries'], queryFn: fetchChallengeCountries, enabled, staleTime: 86_400_000 })
}

export function useFantasyChallengeCompetitions(countryId: number | undefined) {
  return useQuery({
    queryKey: ['fantasy-challenge-create-competitions', countryId],
    queryFn: () => fetchChallengeCompetitions(countryId!),
    enabled: countryId !== undefined,
    staleTime: 1_800_000,
  })
}

export function useFantasyChallengeSeasons(leagueId: number | undefined, countryId?: number) {
  return useQuery({
    queryKey: ['fantasy-challenge-create-seasons', leagueId, countryId],
    queryFn: () => fetchChallengeSeasons(leagueId!, countryId),
    enabled: leagueId !== undefined,
    staleTime: 1_800_000,
  })
}

export function useFantasyChallengeClubs(countryId: number | undefined, leagueId: number | undefined, seasonId: number | undefined) {
  return useQuery({
    queryKey: ['fantasy-challenge-create-clubs', countryId, leagueId, seasonId],
    queryFn: () => fetchChallengeClubs(countryId!, leagueId!, seasonId!),
    enabled: countryId !== undefined && leagueId !== undefined && seasonId !== undefined,
    staleTime: 1_800_000,
  })
}

export function useFantasyChallengeAppRounds(seasonId: number | undefined, leagueId?: number) {
  return useQuery({
    queryKey: ['fantasy-challenge-create-rounds', seasonId, leagueId],
    queryFn: () => fetchChallengeAppRounds(seasonId!, leagueId),
    enabled: seasonId !== undefined,
    staleTime: 300_000,
  })
}

export function useCreateFantasyChallenge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createFantasyChallenge,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fantasy-challenges'] })
    },
  })
}
